import React, { useMemo } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useNews } from '../context/NewsContext';
import { 
    Box, 
    Typography, 
    Container, 
    Divider, 
    Link, 
    Paper,
    Breadcrumbs,
    useTheme,
    alpha
} from '@mui/material';

const SearchPage = () => {
    const [searchParams] = useSearchParams();
    const query = searchParams.get('q') || '';
    const { news } = useNews();
    const theme = useTheme();

    const normalizeText = (text) => {
        return text
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase();
    };

    const results = useMemo(() => {
        if (!query) return [];
        const normalizedQuery = normalizeText(query);

        return news.filter(item => {
            const normalizedTitle = normalizeText(item.title || '');
            const normalizedContent = normalizeText(item.content || '');

            return normalizedTitle.includes(normalizedQuery) ||
                normalizedContent.includes(normalizedQuery);
        });
    }, [query, news]);

    return (
        <Container maxWidth="md" sx={{ py: 8 }}>
            <Breadcrumbs sx={{ mb: 4 }}>
                <Link component={RouterLink} to="/" color="inherit" underline="hover">Início</Link>
                <Typography color="text.primary">Busca</Typography>
            </Breadcrumbs>

            <Box sx={{ mb: 6 }}>
                <Typography variant="h3" component="h1" sx={{ fontWeight: 800, mb: 1, color: '#111' }}>
                    Resultados da Busca
                </Typography>
                <Typography variant="body1" sx={{ color: '#666' }}>
                    Mostrando {results.length} resultados para "<strong>{query}</strong>"
                </Typography>
            </Box>

            <Divider sx={{ mb: 4 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {results.length > 0 ? (
                    results.map(item => (
                        <Paper 
                            key={item.id} 
                            elevation={0} 
                            sx={{ 
                                p: 0, 
                                borderRadius: '12px', 
                                border: '1px solid transparent',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    borderColor: '#eee',
                                    backgroundColor: alpha(theme.palette.primary.main, 0.02)
                                }
                            }}
                        >
                            <Link 
                                component={RouterLink} 
                                to={`/news/${item.id}`} 
                                underline="none"
                                sx={{ display: 'flex', gap: 3, p: 2 }}
                            >
                                {item.image && (
                                    <Box 
                                        component="img"
                                        src={item.image} 
                                        alt={item.title} 
                                        sx={{ 
                                            width: { xs: 80, sm: 120 }, 
                                            height: { xs: 80, sm: 120 }, 
                                            objectFit: 'cover',
                                            borderRadius: '8px',
                                            flexShrink: 0
                                        }} 
                                    />
                                )}
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#111', mb: 1, lineHeight: 1.4 }}>
                                        {item.title}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        {item.content ? item.content.substring(0, 150) + '...' : 'Descrição indisponível.'}
                                    </Typography>
                                </Box>
                            </Link>
                        </Paper>
                    ))
                ) : (
                    <Box sx={{ py: 10, textAlign: 'center', backgroundColor: '#f9f9f9', borderRadius: '16px' }}>
                        <Typography variant="h6" sx={{ color: '#999' }}>Nenhuma notícia encontrada para sua busca.</Typography>
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default SearchPage;
